import React, { useEffect, useState } from "react";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { fetchAdminUserActivity, formatAdminError } from "../adminApi";
import { useTranslation } from "../../../i18n";

const PAGE_SIZE = 25;

const EVENT_ICONS = {
  login_success: "login",
  login_failed: "warning",
  login_2fa_challenge: "pin",
  login_2fa_success: "verified_user",
  login_2fa_failed: "gpp_bad",
  logout: "logout",
  registered: "person_add",
  impersonation_started: "supervisor_account",
  impersonation_stopped: "person_off",
  case_submitted: "gavel",
  ticket_created: "support_agent",
  ticket_replied: "forum",
  review_replied: "reply",
  google_connected: "link",
  google_disconnected: "link_off",
  jameda_connected: "link",
  jameda_disconnected: "link_off",
  notification_prefs_updated: "notifications",
  role_changed: "badge",
  account_status_changed: "account_balance_wallet",
  trial_extended: "event_available",
  two_factor_enabled: "security",
  two_factor_disabled: "no_encryption",
};

function formatDateTime(iso, locale) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(locale === "de" ? "de-DE" : "en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function eventLabel(eventType, t) {
  const key = `adminUserActivity.events.${eventType}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  return eventType.replace(/_/g, " ");
}

function connectionMeta(meta) {
  if (!meta || typeof meta !== "object") return null;
  const platform =
    meta.platform_label ||
    (meta.platform === "google"
      ? "Google Business Profile"
      : meta.platform === "jameda"
        ? "Jameda"
        : meta.platform && meta.platform !== "mixed"
          ? meta.platform
          : "") ||
    "";
  const names = Array.isArray(meta.location_names)
    ? meta.location_names.filter(Boolean)
    : Array.isArray(meta.locations)
      ? meta.locations.map((l) => l?.name).filter(Boolean)
      : meta.location_name
        ? [meta.location_name]
        : meta.business_name
          ? [meta.business_name]
          : [];
  const caseReference = meta.case_reference || "";
  if (!platform && !names.length && !meta.provider_email && !caseReference) {
    return null;
  }
  return {
    platform,
    names,
    providerEmail: meta.provider_email || "",
    caseReference,
  };
}

/**
 * Per-user activity timeline (auth + product) for AdminUserDetailPage.
 */
const AdminUserActivityTimeline = ({ userId }) => {
  const { t, locale } = useTranslation();
  const [category, setCategory] = useState("");
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchAdminUserActivity(userId, {
          category: category || undefined,
          limit: PAGE_SIZE,
          offset: 0,
        });
        if (!active) return;
        setCount(data.count);
        setEvents(data.results);
      } catch (err) {
        if (!active) return;
        setError(formatAdminError(err));
        setEvents([]);
        setCount(0);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [userId, category]);

  const loadMore = async () => {
    if (!userId || loadingMore || events.length >= count) return;
    setLoadingMore(true);
    try {
      const data = await fetchAdminUserActivity(userId, {
        category: category || undefined,
        limit: PAGE_SIZE,
        offset: events.length,
      });
      setCount(data.count);
      setEvents((prev) => [...prev, ...data.results]);
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMore = events.length < count;

  const filters = [
    { value: "", label: t("adminUserActivity.filterAll") },
    { value: "auth", label: t("adminUserActivity.filterAuth") },
    { value: "product", label: t("adminUserActivity.filterProduct") },
  ];

  return (
    <section className="mt-10 rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-soft md:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-headline-md text-headline-md text-primary">
            {t("adminUserActivity.title")}
          </h2>
          <p className="mt-1 text-body-md text-on-surface-variant">
            {t("adminUserActivity.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.value || "all"}
              type="button"
              onClick={() => setCategory(f.value)}
              className={[
                "rounded-full px-3 py-1.5 text-label-sm font-semibold transition",
                category === f.value
                  ? "bg-secondary text-on-secondary"
                  : "border border-outline-variant/40 bg-white text-on-surface-variant hover:border-secondary/40",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <p className="mb-4 rounded-lg border border-error/30 bg-error-container px-4 py-3 text-body-md text-on-error-container">
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="flex flex-col items-center py-12">
          <MaterialIcon name="sync" className="animate-spin text-[32px] text-secondary" />
          <p className="mt-3 text-body-md text-on-surface-variant">
            {t("adminUserActivity.loading")}
          </p>
        </div>
      ) : !events.length ? (
        <p className="rounded-xl border border-dashed border-outline-variant/40 px-4 py-10 text-center text-body-md text-on-surface-variant">
          {t("adminUserActivity.empty")}
        </p>
      ) : (
        <>
          <ol className="relative space-y-0 border-l border-outline-variant/40 ml-3">
            {events.map((ev) => {
              const icon = EVENT_ICONS[ev.eventType] || "history";
              const connection = connectionMeta(ev.metadata);
              return (
                <li key={ev.id} className="relative pb-6 pl-6 last:pb-0">
                  <span
                    className={[
                      "absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-surface-container-lowest",
                      ev.success
                        ? "bg-secondary/15 text-secondary"
                        : "bg-error/15 text-error",
                    ].join(" ")}
                  >
                    <MaterialIcon name={icon} className="text-[14px]" />
                  </span>
                  <div className="min-w-0 rounded-xl border border-outline-variant/25 bg-white px-4 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-label-sm font-semibold text-primary">
                          {ev.summary || eventLabel(ev.eventType, t)}
                        </p>
                        <p className="mt-0.5 text-[12px] text-on-surface-variant">
                          {eventLabel(ev.eventType, t)}
                          <span className="mx-1.5 text-outline">·</span>
                          <span
                            className={
                              ev.category === "auth"
                                ? "text-sky-700"
                                : "text-emerald-800"
                            }
                          >
                            {ev.category === "auth"
                              ? t("adminUserActivity.filterAuth")
                              : t("adminUserActivity.filterProduct")}
                          </span>
                        </p>
                      </div>
                      <time
                        className="shrink-0 text-[12px] text-outline"
                        dateTime={ev.createdAt || undefined}
                      >
                        {formatDateTime(ev.createdAt, locale)}
                      </time>
                    </div>

                    {connection ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {connection.caseReference ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-900 ring-1 ring-inset ring-amber-200">
                            <MaterialIcon name="gavel" className="text-[12px]" />
                            {t("adminUserActivity.case")}: {connection.caseReference}
                          </span>
                        ) : null}
                        {connection.platform ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-0.5 text-[11px] font-semibold text-sky-900 ring-1 ring-inset ring-sky-200">
                            <MaterialIcon name="dns" className="text-[12px]" />
                            {t("adminUserActivity.platform")}: {connection.platform}
                          </span>
                        ) : null}
                        {connection.names.map((name) => (
                          <span
                            key={name}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-900 ring-1 ring-inset ring-emerald-200"
                          >
                            <MaterialIcon name="place" className="text-[12px]" />
                            {t("adminUserActivity.location")}: {name}
                          </span>
                        ))}
                        {connection.providerEmail ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                            <MaterialIcon name="mail" className="text-[12px]" />
                            {connection.providerEmail}
                          </span>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-on-surface-variant">
                      {ev.ipAddress ? (
                        <span>
                          {t("adminUserActivity.ip")}: {ev.ipAddress}
                        </span>
                      ) : null}
                      {ev.actor ? (
                        <span>
                          {t("adminUserActivity.actor")}:{" "}
                          {ev.actor.fullName || ev.actor.email}
                        </span>
                      ) : null}
                      {!ev.success ? (
                        <span className="font-semibold text-error">
                          {t("adminUserActivity.failed")}
                        </span>
                      ) : null}
                    </div>
                    {ev.userAgent ? (
                      <p className="mt-1 truncate text-[11px] text-outline" title={ev.userAgent}>
                        {ev.userAgent}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>

          {hasMore ? (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                disabled={loadingMore}
                onClick={loadMore}
                className="rounded-lg border border-outline-variant/40 bg-white px-5 py-2.5 text-label-sm font-semibold text-on-surface transition hover:border-secondary/40 disabled:opacity-50"
              >
                {loadingMore
                  ? t("adminUserActivity.loadingMore")
                  : t("adminUserActivity.loadMore")}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export default AdminUserActivityTimeline;
